import { useState, type ReactNode } from "react";
import {
  HelpCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Edit3,
  ClipboardCheck,
  MessageSquare,
  Upload,
  Handshake,
} from "lucide-react";
import type { OrderType } from "../types";

// Short, self-contained "how do I…" guide for subcontractors, shown collapsed by default
// at the top of the order view. Each item mirrors a real section on ContractorOrderDetailPage,
// so the wording stays in sync with the buttons the contractor actually sees.
interface InstructionItem {
  id: string;
  icon: ReactNode;
  title: string;
  body: ReactNode;
  /** Pokazywane tylko dla tych typów zamówienia; brak = dla każdego zamówienia. */
  orderTypes?: OrderType[];
}

const INSTRUCTIONS: InstructionItem[] = [
  {
    id: "correspondence",
    icon: (
      <MessageSquare
        size={15}
        className="shrink-0 text-[var(--color-primary)]"
      />
    ),
    title: "Jak korespondować z nami w sprawie zamówienia?",
    body: (
      <>
        Do bieżącej korespondencji służy sekcja{" "}
        <em>„Komentarze i zdarzenia”</em> w prawej sekcji strony. Wpisz treść i
        wyślij; aby oznaczyć konkretną osobę, wpisz <strong>@</strong> i wybierz
        ją z listy. Dane kontaktowe zamawiającego (telefon, e-mail) znajdziesz w
        kafelku <em>„Zamawiający”</em> na górze.
      </>
    ),
  },

  {
    id: "change",
    icon: <Edit3 size={15} className="shrink-0 text-[var(--color-primary)]" />,
    title: "Jak poprosić o zmianę zamówienia?",
    body: (
      <>
        Przejdź do sekcji <em>„Wnioski o zmianę zamówienia”</em> i kliknij{" "}
        <em>„+ Nowy wniosek”</em>. Wybierz typ zmiany, opisz co i dlaczego ma
        ulec zmianie, w razie potrzeby zaznacz pozycje realizacyjne, a następnie
        kliknij <em>„Wyślij wniosek”</em>. Zamawiający po stronie GK ELEKTIM
        rozpatrzy zgłoszenie. Gdy to GK ELEKTIM proponuje zmianę „z prośbą o
        akceptację”, przy wniosku zobaczysz przyciski <em>„Zatwierdź”</em> /{" "}
        <em>„Odrzuć”</em> — zatwierdzenie od razu zmienia zamówienie.
      </>
    ),
  },

  {
    id: "files",
    icon: <Upload size={15} className="shrink-0 text-[var(--color-primary)]" />,
    title: "Jak dodać pliki zamówienia",
    body: (
      <>
        Wyniki prac wgrywasz w sekcji <em>„Pliki pozycji realizacyjnej”</em> —
        przy właściwej pozycji użyj przycisku <em>„Prześlij plik(i)”</em>.
        Maksymalny rozmiar pojedynczego pliku to 200 MB. Przesłane pliki
        trafiają do weryfikacji po stronie GK ELEKTIM.
      </>
    ),
  },

  {
    id: "wpw",
    orderTypes: ["wpw_terenowe", "wpw_kompleksowe"],
    icon: (
      <Handshake size={15} className="shrink-0 text-[var(--color-primary)]" />
    ),
    title: "Jak dodać zgody właścicieli i zmienić status działki (WPW)?",
    body: (
      <>
        Wszystko dzieje się w sekcji{" "}
        <em>„Przekazane WPW (per właściciel)”</em> na tej stronie — jeden wiersz
        to jeden właściciel jednej działki.
        <br />
        <strong>Zmiana statusu:</strong> po prawej stronie wiersza rozwiń listę{" "}
        <em>„Status”</em> i wybierz aktualny stan rozmowy (w trakcie, odmowa /
        żądania, śmierć / niemożliwe zebranie podpisu, pozyskano zgodę pisemną,
        pozyskano decyzję art. 124 UGN). Zapisuje się od razu, a GK ELEKTIM
        widzi zmianę u siebie.
        <br />
        <strong>Skan zgody:</strong> w tym samym wierszu kliknij{" "}
        <em>„Dodaj plik”</em> i wgraj skan / zdjęcie podpisanego dokumentu.
        Statusy oznaczone 📎 (zgoda pisemna, decyzja art. 124) wymagają
        załącznika. Pliki tylko dopisujesz — wcześniejsze zostają w historii.
        <br />
        <strong>Dokumenty od nas:</strong> pozycja <em>„Od GK”</em> to pakiet do
        podpisu u właściciela. Znacznik <em>ZAKTUALIZOWANA</em> oznacza, że
        wgraliśmy nowszą wersję — pobierz ją i kliknij{" "}
        <em>„Oznacz jako przejrzane”</em>. Napis <em>„pakiet nieaktualny”</em>{" "}
        pod statusem oznacza, że otworzyliśmy nowsze zlecenie na tę działkę i
        tego właściciela — dalej pracuj na tamtym pakiecie (odnośnik prowadzi do
        nowszego zlecenia, jeśli trafiło do Twojej firmy).
        <br />
        <strong>Uwagi do konkretnego właściciela:</strong> rozwiń wiersz
        strzałką <em>„&rsaquo;”</em> po lewej — pod spodem jest pole komentarza
        i pełna historia zdarzeń.
        <br />
        Zgodę zatwierdza po naszej stronie GK ELEKTIM — po akceptacji trafia do
        tytułów dostępu właściciela. Rozliczenie prac (POCK) składasz osobno, w
        sekcji <em>„Wnioski o POCK”</em>.
      </>
    ),
  },
  {
    id: "pock",
    icon: (
      <ClipboardCheck
        size={15}
        className="shrink-0 text-[var(--color-primary)]"
      />
    ),
    title: "Jak złożyć prośbę o protokół odbioru (częściowy lub końcowy)?",
    body: (
      <>
        W sekcji <em>„Wnioski o POCK”</em> kliknij <em>„Złóż wniosek”</em>.
        <br />
        <strong>Odbiór częściowy:</strong> w kolumnie <em>„Odebrano”</em> wpisz
        faktycznie wykonane ilości dla poszczególnych pozycji.
        <br />
        <strong>Odbiór całkowity:</strong> rozlicz wszystkie pozycje na 100%.
        Jeśli umowa wymaga pełnego odbioru, wniosek zawsze obejmuje 100%
        wszystkich pozycji.
        <br />
        Dołącz wymagane załączniki i wyślij wniosek — na jego podstawie GK
        ELEKTIM zaakceptuje / odrzuci protokół.
      </>
    ),
  },
  {
    id: "invoice",
    icon: (
      <FileText size={15} className="shrink-0 text-[var(--color-primary)]" />
    ),
    title: "Jak dodać fakturę?",
    body: (
      <>
        Fakturę wystawiasz dopiero po{" "}
        <strong>
          zaakceptowaniu protokołu POCK (protokół odbioru częściowego/końcowego
          / zaliczka){" "}
        </strong>
        . Otwórz protokół z sekcji <em>„Protokoły POCK”</em> na tej stronie (lub
        z zakładki <em>„Protokoły POCK”</em>) i po jego akceptacji użyj
        przycisku <em>„Dodaj fakturę”</em>, podaj numer faktury i wgraj plik
        PDF. Kwota netto faktury musi być zgodna z wartością protokołu.
      </>
    ),
  },
];

export function ContractorInstructionsPanel({
  defaultOpen = false,
  orderType,
}: {
  defaultOpen?: boolean;
  /** Typ zamówienia — ukrywa instrukcje, które go nie dotyczą (np. WPW). */
  orderType?: OrderType;
} = {}) {
  const [panelOpen, setPanelOpen] = useState(defaultOpen);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const items = INSTRUCTIONS.filter(
    (item) =>
      !item.orderTypes || (!!orderType && item.orderTypes.includes(orderType)),
  );

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="mb-6 bg-white dark:bg-gray-800 border-l-4 border-[var(--color-primary)]">
      <button
        onClick={() => setPanelOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors"
      >
        <span className="flex items-center gap-2">
          <HelpCircle size={16} className="text-[var(--color-primary)]" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Instrukcje dla Wykonawcy
          </span>
        </span>
        {panelOpen ? (
          <ChevronUp size={16} className="text-gray-400 shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-gray-400 shrink-0" />
        )}
      </button>

      {panelOpen && (
        <div className="border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          {items.map((item) => {
            const isOpen = openItems.has(item.id);
            return (
              <div key={item.id}>
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {item.icon}
                    <span className="text-sm text-gray-800 dark:text-gray-200 break-words">
                      {item.title}
                    </span>
                  </span>
                  {isOpen ? (
                    <ChevronUp size={14} className="text-gray-400 shrink-0" />
                  ) : (
                    <ChevronDown size={14} className="text-gray-400 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 pl-11 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    {item.body}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
